import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCurrentTestPeriod } from '../hooks/useTestPeriod';
import { useTranslation } from 'react-i18next';
import { Shield, RefreshCw, Image } from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import { authAPI, researchAPI, adminAPI, postAnalysisAPI } from '../services/api';
import DateRangePresets from '../components/DateRangePresets';
import CountryFilter, { type Country } from '../components/CountryFilter';

const GAMBLING_COLORS: Record<string, string> = {
  gambling_pachinko: '#f59e0b',
  gambling_online: '#ef4444',
  gambling_public: '#3b82f6',
  gambling_game: '#8b5cf6',
  gambling_gacha_premium: '#ec4899',
};

const LEGAL_COLORS: Record<string, string> = {
  legal: '#22c55e',
  illegal: '#ef4444',
  gray_zone: '#f59e0b',
};

const fmt = (v?: number | string | null) => {
  if (v === null || v === undefined) return '-';
  const n = typeof v === 'string' ? Number(v) : v;
  return isNaN(n) ? '-' : n.toLocaleString();
};

type Section = 'legal' | 'gallery' | 'accuracy';

export default function GamblingAnalysis() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<Section>('legal');

  // Data
  const [legalStatus, setLegalStatus] = useState<any[]>([]);
  // Corrected post-survey figures, used only to reconcile this page's LIVE classifier
  // numbers against the ones published in the final report. sasadmin-only endpoint —
  // if it is unavailable the banner simply does not render.
  const [corrected, setCorrected] = useState<any | null>(null);
  const [gallery, setGallery] = useState<any>({ data: [], total: 0 });
  const [accuracy, setAccuracy] = useState<any>(null);
  const [advertisers, setAdvertisers] = useState<any[]>([]);

  // Filters — URL params (from a dashboard drill-down) override defaults.
  const [galleryCategory, setGalleryCategory] = useState('all');
  const [galleryOffset, setGalleryOffset] = useState(0);
  const galleryLimit = 30;
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('dateFrom') ?? '');
  const [dateTo, setDateTo] = useState(() => searchParams.get('dateTo') ?? '');
  // Default to the configured current test/survey period (once), unless a URL
  // drill-down specified a range.
  const surveyPeriod = useCurrentTestPeriod();
  const periodAppliedRef = useRef(false);
  useEffect(() => {
    if (periodAppliedRef.current || !surveyPeriod) return;
    periodAppliedRef.current = true;
    if (!searchParams.get('dateFrom')) {
      setDateFrom(surveyPeriod.dateFrom);
      setDateTo(surveyPeriod.dateTo);
    }
  }, [surveyPeriod, searchParams]);
  const [platform, setPlatform] = useState(() => searchParams.get('platform') ?? 'all');
  const [memberCode, setMemberCode] = useState(() => searchParams.get('memberCode') ?? 'all');
  const [country, setCountry] = useState<Country>(() => {
    const v = searchParams.get('country');
    return v === 'JP' || v === 'VN' ? v : 'all';
  });
  const [memberOptions, setMemberOptions] = useState<{ code: string; memberName: string | null }[]>([]);

  // Date inputs are plain JST calendar dates, but the API compares them as timestamptz —
  // a bare '2026-07-06' is UTC midnight = 09:00 JST, silently dropping 9h at each end
  // (survey window read 1,863 instead of 1,916). Send explicit JST-bounded instants.
  const jstFrom = (d: string) => `${d}T00:00:00+09:00`;
  const jstTo   = (d: string) => `${d}T23:59:59+09:00`;

  const dateOpts = () => ({
    dateFrom: dateFrom ? jstFrom(dateFrom) : undefined,
    dateTo:   dateTo   ? jstTo(dateTo)     : undefined,
    platform: platform !== 'all' ? platform : undefined,
    memberCode: memberCode !== 'all' ? memberCode : undefined,
    country: country !== 'all' ? country : undefined,
  });

  useEffect(() => {
    adminAPI.getCodesWithAdCounts()
      .then(rows => setMemberOptions(rows))
      .catch(() => setMemberOptions([]));
  }, []);

  function resetFilters() {
    setDateFrom('');
    setDateTo('');
    setPlatform('all');
    setMemberCode('all');
  }

  const loadLegalStatus = useCallback(async () => {
    try {
      const data = await researchAPI.getLegalStatus(dateOpts());
      setLegalStatus(data);
    } catch (e) { console.error(e); }
  }, [dateFrom, dateTo]);

  // Pull the corrected post-survey summary once, so the banner can show the published
  // figures next to this page's live ones. Silent failure is intentional (non-sasadmin).
  useEffect(() => {
    let cancelled = false;
    postAnalysisAPI.getOverview()
      .then(d => { if (!cancelled) setCorrected(d); })
      .catch(() => { /* not sasadmin, or no run yet — banner stays hidden */ });
    return () => { cancelled = true; };
  }, []);

  const loadGallery = useCallback(async (offset = 0) => {
    try {
      const data = await researchAPI.getGamblingGallery({
        category: galleryCategory !== 'all' ? galleryCategory : undefined,
        limit: galleryLimit,
        offset,
        ...dateOpts(),
      });
      setGallery(data);
      setGalleryOffset(offset);
    } catch (e) { console.error(e); }
  }, [galleryCategory, dateFrom, dateTo]);

  const loadAccuracy = useCallback(async () => {
    try {
      const data = await researchAPI.getClassificationAccuracy();
      setAccuracy(data);
    } catch (e) { console.error(e); }
  }, []);

  const loadAdvertisers = useCallback(async () => {
    try {
      const data = await researchAPI.getAdvertiserProfiles(dateOpts());
      setAdvertisers(data);
    } catch (e) { console.error(e); }
  }, [dateFrom, dateTo]);

  const reloadAll = useCallback(() => {
    setLoading(true);
    Promise.all([loadLegalStatus(), loadGallery(0), loadAccuracy(), loadAdvertisers()])
      .finally(() => setLoading(false));
  }, [loadLegalStatus, loadGallery, loadAccuracy, loadAdvertisers]);

  // Auth first — do NOT load data from inside this effect. It has empty deps, so the
  // loaders it closed over at mount still carry the pre-default (empty) date range; the
  // async auth check resolves *after* the survey-period default lands, and that stale
  // unfiltered request then overwrites the correctly-filtered one. Load below instead.
  const [authed, setAuthed] = useState(false);
  useEffect(() => {
    authAPI.checkAdminAuth().then(r => {
      if (!r.authenticated) navigate('/admin');
      else setAuthed(true);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (authed) loadGallery(0); }, [galleryCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Single source of loading: runs once authed, and again whenever a filter changes —
  // including the asynchronously-applied default survey period.
  useEffect(() => {
    if (!authed) return;
    reloadAll();
  }, [authed, dateFrom, dateTo, platform, memberCode, country]); // eslint-disable-line react-hooks/exhaustive-deps

  // Legal status pie
  // Data uses snake_case ('gray_zone') but the locale key is camelCase ('grayZone'),
  // so a bare t(`research.${name}`) rendered the raw key on screen.
  const legalLabel = (name: string) =>
    t(`research.${name === 'gray_zone' ? 'grayZone' : name}` as any) || name;
  // '件' is a Japanese counter — omit it in English rather than printing "36件".
  const unit = t('common.adsUnit', '件');

  const legalPie = (() => {
    const m: Record<string, number> = {};
    legalStatus.forEach(d => {
      m[d.legalStatus] = (m[d.legalStatus] || 0) + d.adCount;
    });
    return Object.entries(m)
      .filter(([k]) => k !== 'unknown')
      .map(([name, value]) => ({ name, value }));
  })();

  // Category breakdown pie
  const categoryPie = (() => {
    const m: Record<string, number> = {};
    legalStatus.forEach(d => {
      m[d.category] = (m[d.category] || 0) + d.adCount;
    });
    return Object.entries(m).map(([name, value]) => ({ name, value }));
  })();

  const SectionButton = ({ id, label }: { id: Section; label: string }) => (
    <button onClick={() => setActiveSection(id)}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        activeSection === id
          ? 'bg-red-600 text-white'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
      }`}>
      {label}
    </button>
  );

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header — title row */}
        <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Shield className="w-7 h-7 text-red-500" />
              {t('gamblingAnalysis.title')}
            </h1>
          </div>
          <button onClick={reloadAll}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-semibold">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </button>
        </div>
        {/* Reconciliation notice — this page shows the LIVE classifier output, which is
            deliberately NOT the corrected figure published in the final report. Without this
            the same survey reads 1,916 here and 1,328 on the post-survey page. */}
        {(() => {
          // Window comes from the test-period endpoint (readable by ANY admin) so the caveat
          // still appears for sys-operator; the exact figures come from the sasadmin-only
          // post-analysis endpoint and are simply omitted when unavailable.
          const s = corrected?.hasData ? corrected.summary : null;
          const wFrom = corrected?.run ? String(corrected.run.windowFrom).slice(0, 10) : surveyPeriod?.dateFrom;
          const wToIncl = corrected?.run
            ? new Date(new Date(String(corrected.run.windowTo).slice(0, 10)).getTime() - 86400000).toISOString().slice(0, 10)
            : surveyPeriod?.dateTo;
          if (!wFrom || !wToIncl) return null;
          // only meaningful while the user is looking at (part of) the survey window
          const overlaps = (!dateFrom || dateFrom <= wToIncl) && (!dateTo || dateTo >= wFrom);
          if (!overlaps) return null;
          return (
            <div className="mb-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
              <div className="flex items-start gap-2">
                <span className="text-amber-600 dark:text-amber-400 text-lg leading-none">⚠</span>
                <div className="text-sm text-amber-900 dark:text-amber-100">
                  <div className="font-semibold mb-1">
                    {t('gamblingAnalysis.liveVsCorrectedTitle',
                       'この画面は「補正前」のライブ分類結果です / This page shows the LIVE, uncorrected classification')}
                  </div>
                  <div className="leading-relaxed">
                    {s
                      ? t('gamblingAnalysis.liveVsCorrectedBody', {
                          defaultValue:
                            '最終報告書および「調査後 高精度分析」では、全広告主の監査により誤検知を除いた補正値を採用しています。調査期間 {{from}}〜{{to}} の確定値: ギャンブル {{baseline}}件 → 補正後 {{gambling}}件、うち違法カジノ {{illegal}}件（METAWIN.COM のみ）。',
                          from: wFrom, to: wToIncl,
                          baseline: Number(s.baselineGambling).toLocaleString(),
                          gambling: Number(s.gambling).toLocaleString(),
                          illegal: s.illegal,
                        })
                      : t('gamblingAnalysis.liveVsCorrectedShort', {
                          defaultValue:
                            '最終報告書では、調査期間 {{from}}〜{{to}} について全広告主を監査し、誤検知を除いた補正値を採用しています。本画面の件数はそれより多くなります。',
                          from: wFrom, to: wToIncl,
                        })}
                  </div>
                  <button
                    onClick={() => navigate('/admin/post-survey-analysis')}
                    className="mt-2 px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold">
                    {t('gamblingAnalysis.openPostSurvey', '調査後 高精度分析を開く / Open Post-Survey Analysis')}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Filter row — single horizontal toolbar */}
        <div className="flex items-center gap-2 flex-wrap mb-6 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
          <CountryFilter value={country} onChange={setCountry} />
          <span className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
          <DateRangePresets
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={(f, to) => { setDateFrom(f); setDateTo(to); }}
            surveyPeriod={surveyPeriod ? { from: surveyPeriod.dateFrom, to: surveyPeriod.dateTo } : null}
          />
          <span className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
          <select value={platform} onChange={e => setPlatform(e.target.value)}
            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-xs">
            <option value="all">{t('research.allPlatforms')}</option>
            <option value="youtube">YouTube</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
            <option value="x_twitter">X (Twitter)</option>
            <option value="tiktok">TikTok</option>
          </select>
          <select value={memberCode} onChange={e => setMemberCode(e.target.value)}
            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-xs max-w-[180px]">
            <option value="all">{t('research.allVolunteers', 'All Volunteers')}</option>
            {memberOptions.map(m => (
              <option key={m.code} value={m.code}>
                {m.memberName ? `${m.memberName} (${m.code})` : m.code}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={resetFilters}
            className="px-2.5 py-1 rounded text-xs font-semibold bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200"
          >
            {t('common.reset')}
          </button>
        </div>

        {/* Section Tabs */}
        <div className="flex gap-2 mb-6">
          <SectionButton id="legal" label={t('gamblingAnalysis.legalStatusDashboard')} />
          <SectionButton id="gallery" label={t('gamblingAnalysis.adGallery')} />
          <SectionButton id="accuracy" label={t('gamblingAnalysis.classificationAccuracy')} />
        </div>

        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-20 flex items-center justify-center text-gray-400">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" /> {t('common.loading')}
          </div>
        ) : (
          <>
            {/* SECTION: Legal Status */}
            {activeSection === 'legal' && (
              <div className="space-y-6">
                {(() => {
                  const totalGambling = legalPie.reduce((s, d) => s + (d.value as number), 0);
                  if (totalGambling === 0) {
                    return (
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded text-sm text-blue-900 dark:text-blue-200">
                        ℹ {t('sampleWarning.noData')}
                      </div>
                    );
                  }
                  if (totalGambling < 50) {
                    return (
                      <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 rounded text-xs text-yellow-900 dark:text-yellow-200">
                        ⚠ {t('sampleWarning.lowSample', { count: totalGambling })}
                      </div>
                    );
                  }
                  return null;
                })()}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Legal vs Illegal Pie (clickable → CapturedAds) */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('research.legalVsIllegal')}</h3>
                    {legalPie.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            <Pie data={legalPie} cx="50%" cy="50%" outerRadius={90} innerRadius={50} dataKey="value" nameKey="name"
                              onClick={(data: any) => {
                                const catFilter = data.name === 'illegal' ? 'gambling_illegal'
                                                 : data.name === 'legal' ? 'gambling_legal'
                                                 : data.name === 'gray_zone' ? 'gambling_gray'
                                                 : 'gambling_all';
                                const qs = new URLSearchParams();
                                qs.set('category', catFilter);
                                if (dateFrom) qs.set('dateFrom', dateFrom);
                                if (dateTo)   qs.set('dateTo',   dateTo);
                                if (platform !== 'all')   qs.set('platform', platform);
                                if (memberCode !== 'all') qs.set('memberCode', memberCode);
                                qs.set('country', 'all');
                                qs.set('eventType', 'all');
                                qs.set('enrichment', 'all');
                                navigate(`/admin/captured-ads?${qs.toString()}`);
                              }}
                              style={{ cursor: 'pointer' }}
                              label={({ name, value, percent }) => `${legalLabel(name)} ${value}${unit} (${(percent * 100).toFixed(0)}%)`}>
                              {legalPie.map(entry => (
                                <Cell key={entry.name} fill={LEGAL_COLORS[entry.name] || '#6b7280'} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v: any, n: any) => [`${v}${unit}`, legalLabel(n)]} />
                          </PieChart>
                        </ResponsiveContainer>
                        <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                          💡 {t('research.clickPieHint', 'Click any slice to view those individual ads')}
                        </p>
                      </>
                    ) : <p className="text-gray-400 text-center py-10">{t('research.noDataAvailable')}</p>}
                  </div>

                  {/* Category Breakdown Pie (clickable) */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('research.category')}</h3>
                    {categoryPie.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            <Pie data={categoryPie} cx="50%" cy="50%" outerRadius={90} dataKey="value" nameKey="name"
                              onClick={(data: any) => {
                                // Data.name is the sub-category (gambling_online, gambling_pachinko, etc.)
                                const qs = new URLSearchParams();
                                qs.set('category', data.name);
                                if (dateFrom) qs.set('dateFrom', dateFrom);
                                if (dateTo)   qs.set('dateTo',   dateTo);
                                if (platform !== 'all')   qs.set('platform', platform);
                                if (memberCode !== 'all') qs.set('memberCode', memberCode);
                                qs.set('country', 'all');
                                qs.set('eventType', 'all');
                                qs.set('enrichment', 'all');
                                navigate(`/admin/captured-ads?${qs.toString()}`);
                              }}
                              style={{ cursor: 'pointer' }}
                              label={({ name, value, percent }) => `${t(`categories.${name}` as any) || name} ${value}${unit} (${(percent * 100).toFixed(0)}%)`}>
                              {categoryPie.map(entry => (
                                <Cell key={entry.name} fill={GAMBLING_COLORS[entry.name] || '#6b7280'} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v: any, n: any) => [`${v}${unit}`, t(`categories.${n}` as any) || n]} />
                          </PieChart>
                        </ResponsiveContainer>
                        <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                          💡 {t('research.clickPieHint', 'Click any slice to view those individual ads')}
                        </p>
                      </>
                    ) : null}
                  </div>
                </div>

                {/* Top Advertisers */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('gamblingAnalysis.advertiserProfiles')}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm table-fixed">
                      <thead>
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400 w-[30%]">{t('research.advertiser')}</th>
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400 w-[22%]">{t('research.category')}</th>
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400 w-[12%]">{t('research.platform')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.impressions')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.reach')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.avgConfidence')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {advertisers.slice(0, 20).map((a, i) => (
                          <tr key={i} className="border-b dark:border-gray-700/50">
                            <td className="py-2 px-3 font-medium text-gray-900 dark:text-white">
                              {a.advertiser}
                              {a.domain && <span className="ml-2 text-xs text-gray-400 font-mono">{a.domain}</span>}
                            </td>
                            <td className="py-2 px-3">
                              <span className="px-2 py-0.5 rounded text-xs font-semibold"
                                style={{ backgroundColor: (GAMBLING_COLORS[a.category] || '#6b7280') + '20', color: GAMBLING_COLORS[a.category] || '#6b7280' }}>
                                {t(`categories.${a.category}` as any) || a.category}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-gray-700 dark:text-gray-300">{a.platform}</td>
                            <td className="py-2 px-3 text-right font-semibold text-gray-900 dark:text-white">{fmt(a.impressions)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{fmt(a.reach)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{a.avgConfidence || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* SECTION: Gallery */}
            {activeSection === 'gallery' && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('gamblingAnalysis.adGallery')}</h3>
                  <select value={galleryCategory} onChange={e => setGalleryCategory(e.target.value)}
                    className="px-3 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm">
                    <option value="all">{t('capturedAds.allCategories')}</option>
                    {Object.keys(GAMBLING_COLORS).map(cat => (
                      <option key={cat} value={cat}>{t(`categories.${cat}` as any)}</option>
                    ))}
                  </select>
                </div>

                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {fmt(gallery.total)} {t('research.gamblingAds')}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {gallery.data?.map((ad: any) => (
                    <div key={ad.id} className="border dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      {ad.thumbnailUrl ? (
                        <img src={ad.thumbnailUrl} alt={ad.adTitle || ''} className="w-full h-36 object-cover bg-gray-100 dark:bg-gray-700" />
                      ) : (
                        <div className="w-full h-36 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          <Image className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                        </div>
                      )}
                      <div className="p-3">
                        <p className="font-medium text-sm text-gray-900 dark:text-white line-clamp-1">{ad.advertiserName || ad.advertiserDomain || 'Unknown'}</p>
                        {ad.adTitle && <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">{ad.adTitle}</p>}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="px-2 py-0.5 rounded text-xs font-semibold"
                            style={{ backgroundColor: (GAMBLING_COLORS[ad.category] || '#6b7280') + '20', color: GAMBLING_COLORS[ad.category] || '#6b7280' }}>
                            {t(`categories.${ad.category}` as any)}
                          </span>
                          <span className="text-xs text-gray-400">{ad.platform}</span>
                          {ad.confidence && <span className="text-xs text-gray-400">{(Number(ad.confidence) * 100).toFixed(0)}%</span>}
                          {ad.isManuallyClassified && <span className="text-xs text-blue-500 font-semibold">Manual</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {gallery.total > galleryLimit && (
                  <div className="flex justify-center gap-3 mt-6">
                    <button disabled={galleryOffset === 0} onClick={() => loadGallery(Math.max(0, galleryOffset - galleryLimit))}
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm disabled:opacity-50">
                      {t('capturedAds.previous')}
                    </button>
                    <span className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                      {galleryOffset + 1}-{Math.min(galleryOffset + galleryLimit, gallery.total)} / {gallery.total}
                    </span>
                    <button disabled={galleryOffset + galleryLimit >= gallery.total} onClick={() => loadGallery(galleryOffset + galleryLimit)}
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm disabled:opacity-50">
                      {t('capturedAds.next')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* SECTION: Accuracy */}
            {activeSection === 'accuracy' && accuracy && (
              <div className="space-y-6">
                {/* Totals */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700 text-center">
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">{fmt(accuracy.totals?.totalGambling)}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('gamblingAnalysis.totalGambling')}</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700 text-center">
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{fmt(accuracy.totals?.manuallyClassified)}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('gamblingAnalysis.manuallyClassified')}</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700 text-center">
                    <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{fmt(accuracy.totals?.gamblingManual)}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('gamblingAnalysis.corrections')} (Gambling)</p>
                  </div>
                </div>

                {/* Confidence Distribution */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('gamblingAnalysis.confidenceDistribution')}</h3>
                  {accuracy.confidenceDistribution?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={accuracy.confidenceDistribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="confidenceBucket" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: 8, color: '#fff' }} />
                        <Legend />
                        <Bar dataKey="count" fill="#f59e0b" name={t('gamblingAnalysis.count')} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : null}
                </div>

                {/* Classification Source */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('gamblingAnalysis.classificationSource')}</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b dark:border-gray-700">
                        <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400">{t('gamblingAnalysis.source')}</th>
                        <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('gamblingAnalysis.count')}</th>
                        <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.avgConfidence')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accuracy.sourceDistribution?.map((s: any) => (
                        <tr key={s.source} className="border-b dark:border-gray-700/50">
                          <td className="py-2 px-3 text-gray-900 dark:text-white font-medium">{s.source}</td>
                          <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{fmt(s.count)}</td>
                          <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{s.avgConfidence || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Correction History */}
                {accuracy.corrections?.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('gamblingAnalysis.corrections')}</h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400">From</th>
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400">To</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('gamblingAnalysis.count')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accuracy.corrections.map((c: any, i: number) => (
                          <tr key={i} className="border-b dark:border-gray-700/50">
                            <td className="py-2 px-3 text-red-600 dark:text-red-400">{t(`categories.${c.oldCategory}` as any) || c.oldCategory}</td>
                            <td className="py-2 px-3 text-green-600 dark:text-green-400">{t(`categories.${c.newCategory}` as any) || c.newCategory}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{c.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
